
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Activity, ActivityCategory } from '@/types/program';

interface ActivityDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<Activity, 'id' | 'created_at' | 'updated_at' | 'program_id' | 'order_index'>) => void;
  activity?: Activity | null;
}

const categoryOptions: { value: ActivityCategory; label: string }[] = [
  { value: 'warm_up', label: 'Warm Up' },
  { value: 'drill', label: 'Drill' },
  { value: 'conditioning', label: 'Conditioning' },
  { value: 'scrimmage', label: 'Scrimmage' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'cool_down', label: 'Cool Down' },
  { value: 'other', label: 'Other' }
];

export const ActivityDialog = ({ isOpen, onClose, onSubmit, activity }: ActivityDialogProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ActivityCategory>('drill');
  const [duration, setDuration] = useState(10);
  const [embedUrl, setEmbedUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (activity) {
      setTitle(activity.title);
      setDescription(activity.description || '');
      setCategory(activity.category);
      setDuration(activity.duration);
      setEmbedUrl(activity.embed_url || '');
    } else {
      setTitle('');
      setDescription('');
      setCategory('drill');
      setDuration(10);
      setEmbedUrl('');
    }
  }, [activity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || duration <= 0) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        category,
        duration,
        embed_url: embedUrl.trim() || undefined
      });
    } catch (error) {
      console.error('Error submitting activity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {activity ? 'Edit Activity' : 'Add Activity'}
          </DialogTitle>
          <DialogDescription>
            {activity 
              ? 'Update the activity details below.'
              : 'Add a new activity to your training program.'
            }
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Activity Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter activity title..."
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select value={category} onValueChange={(value: ActivityCategory) => setCategory(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">Duration (minutes)</Label>
            <Input
              id="duration"
              type="number"
              min="1"
              max="180"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter activity description (optional)..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="embedUrl">Video Embed URL (Optional)</Label>
            <Input
              id="embedUrl"
              value={embedUrl}
              onChange={(e) => setEmbedUrl(e.target.value)}
              placeholder="YouTube, Vimeo, or other video embed URL..."
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !title.trim() || duration <= 0}>
              {isSubmitting ? 'Saving...' : (activity ? 'Update' : 'Add')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
